/**
 * Detection / Rules - "Live payload classification" (Bank II-7, III-44).
 *
 * Scope: the pgwire Decision Gateway, so SQL statements and result rows on
 * the PostgreSQL wire protocol, Simple Query only. Not HTTP or REST API
 * payloads, not Extended protocol statements; the panel says so.
 *
 * The DDR gateway classifies a sample of the request and response payloads
 * it forwards with the Discovery classifier and reports counts per route (a
 * table name). It walks every statement of a message in statement order
 * (joins, CTEs, subqueries, UNION); a statement is sampled under the rule of
 * the first table with its own rule (else `*`), and what it found is counted
 * on every covered table it touched. An off rule excludes its table even
 * under `*`. This panel sets the per-route sample rate and size cap, shows per
 * rule what was seen and what was NOT classified (coverage gaps) with a
 * staleness flag, and what the gateway found over the last seven days: data
 * classes and counts, payloads skipped over the cap, and the classifier's own
 * time. No value is ever shown: none is stored.
 */

import { useCallback, useEffect, useState } from 'react';
import { Radar, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCan } from '@/lib/access';
import {
  PAYLOAD_ROUTE_WRITE_PERMISSION,
  STALE_AFTER_MS,
  coverageByRule,
  dataClassesLabel,
  isRuleActive,
  isStale,
  tablesTruncatedCount,
  unattributedCount,
  type RuleCoverage,
  deletePayloadRoute,
  getPayloadSummary,
  listPayloadRoutes,
  routeInputError,
  savePayloadRoute,
  type PayloadRoute,
  type PayloadRouteSummary,
} from '@/lib/payload-classification-api';

const INPUT_CLASS = 'glass-lite rounded-md px-2 py-1 bg-transparent border border-border/40 text-xs';

function istTime(iso: string): string {
  return `${new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`;
}

export function PayloadClassificationPanel() {
  const canWrite = useCan(PAYLOAD_ROUTE_WRITE_PERMISSION);
  const [routes, setRoutes] = useState<PayloadRoute[]>([]);
  const [summary, setSummary] = useState<PayloadRouteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [route, setRoute] = useState('');
  const [ratePercent, setRatePercent] = useState('10');
  const [capKb, setCapKb] = useState('64');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([listPayloadRoutes(), getPayloadSummary()]);
      setRoutes(r);
      setSummary(s.routes);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onSave() {
    const problem = routeInputError(route, ratePercent, capKb);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      await savePayloadRoute({
        route: route.trim().toLowerCase(),
        sampleRate: Number(ratePercent) / 100,
        maxPayloadBytes: Math.round(Number(capKb) * 1024),
        enabled,
      });
      setRoute('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: string) {
    setBusy(true);
    try {
      await deletePayloadRoute(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <GlassCard className="mt-6" data-testid="payload-classification-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radar className="h-4 w-4" /> Live payload classification
        </CardTitle>
        <PayloadScopeNote />
      </CardHeader>

      <div className="px-5 pb-5 space-y-4 text-xs">
        {error && (
          <div className="glass-lite border border-sev-high/40 rounded-md p-3 flex items-start gap-2" role="alert">
            <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {canWrite ? (
          <div className="flex flex-wrap items-end gap-3" data-testid="payload-route-form">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Route (table or *)</span>
              <input className={INPUT_CLASS} value={route} onChange={(e) => setRoute(e.target.value)} placeholder="customers" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sample rate %</span>
              <input className={`${INPUT_CLASS} w-20`} inputMode="decimal" value={ratePercent} onChange={(e) => setRatePercent(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Size cap KB</span>
              <input className={`${INPUT_CLASS} w-20`} inputMode="numeric" value={capKb} onChange={(e) => setCapKb(e.target.value)} />
            </label>
            <label className="flex items-center gap-1.5 pb-1">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>Enabled</span>
            </label>
            <Button size="sm" onClick={onSave} disabled={busy || !route.trim()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save rule
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground">Rules are set by the Data Protection Officer or the Legal Reviewer.</p>
        )}

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : (
          <>
            <PayloadRulesTable
              routes={routes}
              canWrite={canWrite}
              busy={busy}
              onDelete={onDelete}
              coverage={coverageByRule(routes, summary)}
              unattributed={unattributedCount(summary)}
              tablesTruncated={tablesTruncatedCount(summary)}
              nowMs={Date.now()}
            />
            <PayloadSummaryTable summary={summary} />
          </>
        )}
      </div>
    </GlassCard>
  );
}

/**
 * What the panel says the gateway classifies, and what it does not. It must
 * never read as if HTTP or REST API payloads were classified: they are not.
 */
export function PayloadScopeNote() {
  return (
    <>
      <CardDescription>
        The DDR database gateway runs the Discovery classifier over a sample of the SQL statements and result rows it
        forwards (PostgreSQL wire protocol, simple queries) and reports data classes and counts per route (a table
        name). It reads every table a statement touches, in the order the statement names them, including joins,
        CTEs, subqueries, UNION and each statement of a multi-statement query. A statement is sampled under the rule of
        the first of its tables that has its own rule (otherwise the * rule), and what it finds is counted on every
        ruled table the statement touched, so a join shows on each table. A table without its own rule follows the *
        rule; a rule that is off (0% or disabled) excludes its table even when * is set; with no rule at all nothing is
        sampled. Values are never stored. Changes reach the gateway within 30 seconds. At most 200 rules.
      </CardDescription>
      <CardDescription>
        COPY ... TO STDOUT and COPY ... FROM STDIN data is read in text and CSV format. FETCH from a cursor and
        EXECUTE of a prepared statement are counted on the tables of the query the same connection declared them
        with.
      </CardDescription>
      <CardDescription data-testid="payload-classification-scope">
        Not covered: HTTP or REST API traffic, and prepared statements sent with the extended query protocol
        (Parse/Bind/Execute), which most database drivers use for parameterised queries. Shown as not read: COPY in
        binary format, COPY to or from a file or program on the database server, and BINARY cursors. Attribution is by
        the table names a statement writes, so a view over a ruled table, a function that reads one, or a cursor or
        prepared statement declared elsewhere (in a function, or on another connection) is not counted on that table:
        the last is counted as not attributed.
      </CardDescription>
      <CardDescription data-testid="payload-classification-copy-masking">
        COPY and masking: a COPY ... TO or pg_dump that names a column a SELECT masks is refused (permission denied,
        with a Request ID), because COPY output cannot be masked. A cursor read (DECLARE ... CURSOR) that names such a
        column is refused the same way. A COPY ... TO or cursor declaration the gateway cannot read (for example
        FORCE_QUOTE *, COPY BINARY ... TO, or FROM ONLY schema.table) is refused on every tenant, even when it reads
        nothing masked, and the error says what to change; so pg_dump --inserts, --column-inserts and --rows-per-insert
        are refused for every table until issue #244 is fixed (use plain pg_dump). A whole-table COPY of a table
        Discovery classifies as holding personal data is refused, as SELECT * is. Not covered yet (issue #244): a
        whole-table COPY of a table Discovery has not classified is not refused, and its masked columns come back in
        clear text, as with SELECT * (a whole-table cursor, DECLARE ... CURSOR FOR SELECT *, the same). A COPY of
        columns nothing masks, every COPY FROM (a load), and DO blocks, functions and procedures that declare variables
        still run. One exception: on a tenant whose Discovery classifies any table, under the default unparsed policy
        (decide), a COPY FROM or such a DO block that the gateway cannot read is refused as unparsed (issue #209); a
        tenant that classifies nothing, or a gateway run with --unparsed-policy forward, lets it run.
      </CardDescription>
    </>
  );
}

/** The route rules. Delete is offered only to a role that may set rules. */
export function PayloadRulesTable({
  routes,
  canWrite,
  busy,
  onDelete,
  coverage,
  unattributed = 0,
  tablesTruncated = 0,
  nowMs = Date.now(),
}: {
  routes: PayloadRoute[];
  canWrite: boolean;
  busy: boolean;
  onDelete: (id: string) => void;
  coverage?: Map<string, RuleCoverage>;
  unattributed?: number;
  tablesTruncated?: number;
  nowMs?: number;
}) {
  return (
    <>
    {unattributed > 0 && (
      <p className="text-sev-medium" data-testid="payload-unattributed">
        {unattributed.toLocaleString('en-IN')} statement{unattributed === 1 ? '' : 's'} in the last 7 days could not be read
        by the gateway and named no table with its own rule, or fetched from a cursor (or executed a prepared statement)
        the gateway did not see declared: not classified and not counted against any rule.
      </p>
    )}
    {tablesTruncated > 0 && (
      <p className="text-sev-medium" data-testid="payload-tables-truncated">
        {tablesTruncated.toLocaleString('en-IN')} table reference{tablesTruncated === 1 ? '' : 's'} in the last 7 days were
        past the gateway's per-statement limits (more than 8 ruled tables, or more than 64 tables, in one statement):
        not counted against their rules.
      </p>
    )}
    <table className="w-full" data-testid="payload-route-rules">
      <thead className="text-[10px] uppercase tracking-wider text-muted-foreground text-left">
        <tr>
          <th className="py-1">Route</th>
          <th>Sample rate</th>
          <th>Size cap</th>
          <th>State</th>
          <th>Coverage, last 7 days</th>
          <th>Changed</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {routes.length === 0 && (
          <tr>
            <td colSpan={7} className="py-2 text-muted-foreground">No rules: the gateway samples nothing.</td>
          </tr>
        )}
        {routes.map((r) => (
          <tr key={r.id} className="border-t border-border/30">
            <td className="py-1.5 font-mono">{r.route}</td>
            <td>{(r.sampleRate * 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%</td>
            <td>{Math.round(r.maxPayloadBytes / 1024).toLocaleString('en-IN')} KB</td>
            <td>
              <Badge variant={isRuleActive(r) ? 'success' : 'glass'}>{isRuleActive(r) ? 'sampling' : 'off'}</Badge>
            </td>
            <td data-testid={`payload-coverage-${r.route}`}>
              <RuleCoverageCell rule={r} coverage={coverage?.get(r.id)} nowMs={nowMs} />
            </td>
            <td className="text-muted-foreground">
              {r.updatedByUserId}, {istTime(r.updatedAt)}
            </td>
            <td className="text-right">
              {canWrite && (
                <Button variant="glass" size="sm" onClick={() => onDelete(r.id)} disabled={busy} aria-label={`Delete rule ${r.route}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </>
  );
}

/**
 * Per rule: statements seen, and every way a statement on its tables was NOT
 * classified, so an operator can see a coverage gap rather than assume one is
 * not there; and a flag when a sampling rule has not reported for an hour.
 */
export function RuleCoverageCell({ rule, coverage, nowMs }: { rule: PayloadRoute; coverage?: RuleCoverage; nowMs: number }) {
  if (!isRuleActive(rule)) {
    return (
      <span className="text-muted-foreground">
        {rule.route === '*' ? 'Off: tables without their own rule are not sampled.' : 'Off: not sampled, even when * is set.'}
      </span>
    );
  }
  const c = coverage;
  const stale = isStale(rule, c, nowMs);
  const gaps: string[] = [];
  if (c) {
    if (c.unparsed) gaps.push(`${c.unparsed.toLocaleString('en-IN')} the gateway could not read`);
    if (c.extendedProtocol) gaps.push(`${c.extendedProtocol.toLocaleString('en-IN')} prepared (extended protocol)`);
    if (c.skippedBusy) gaps.push(`${c.skippedBusy.toLocaleString('en-IN')} dropped, classifier busy`);
    if (c.skippedOverCap) gaps.push(`${c.skippedOverCap.toLocaleString('en-IN')} payloads over the size cap`);
    if (c.notRead)
      gaps.push(`${c.notRead.toLocaleString('en-IN')} payloads not read (binary or server-side COPY, binary cursor)`);
  }
  return (
    <div className="space-y-0.5">
      {c && c.routes > 0 ? (
        <div>
          Seen {c.seen.toLocaleString('en-IN')}, sampled {c.sampled.toLocaleString('en-IN')}
          {c.multiStatement ? `, ${c.multiStatement.toLocaleString('en-IN')} multi-statement` : ''}
          {c.shared ? `, ${c.shared.toLocaleString('en-IN')} shared with another ruled table` : ''}
        </div>
      ) : null}
      {gaps.length > 0 && <div className="text-sev-medium">Not classified: {gaps.join('; ')}</div>}
      {stale ? (
        <div className="text-sev-high flex items-start gap-1" data-testid={`payload-stale-${rule.route}`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            {c?.lastReceivedAt
              ? `No report since ${istTime(c.lastReceivedAt)} (over ${Math.round(STALE_AFTER_MS / 60_000)} minutes).`
              : 'No report in the last 7 days.'}{' '}
            The table may be idle, or the gateway is not reporting: check foctta_gateway_payload_report_failures_in_a_row
            on the gateway /metrics.
          </span>
        </div>
      ) : (
        c?.lastReceivedAt && <div className="text-muted-foreground">Last report {istTime(c.lastReceivedAt)}</div>
      )}
    </div>
  );
}

/** What the gateway found per route and direction: counts and labels only. */
export function PayloadSummaryTable({ summary }: { summary: PayloadRouteSummary[] }) {
  return (
    <table className="w-full" data-testid="payload-route-summary">
      <thead className="text-[10px] uppercase tracking-wider text-muted-foreground text-left">
        <tr>
          <th className="py-1">Route</th>
          <th>Direction</th>
          <th>Sampled</th>
          <th>Classified</th>
          <th>Over the cap</th>
          <th>Busy</th>
          <th>Not read</th>
          <th>Data classes found</th>
          <th>Classifier time</th>
          <th>Last report</th>
        </tr>
      </thead>
      <tbody>
        {summary.filter((s) => s.route !== '*').length === 0 && (
          <tr>
            <td colSpan={10} className="py-2 text-muted-foreground">No gateway report in the last 7 days.</td>
          </tr>
        )}
        {summary.filter((s) => s.route !== '*').map((s) => (
          <tr key={`${s.route}:${s.direction}`} className="border-t border-border/30">
            <td className="py-1.5 font-mono">{s.route}</td>
            <td>{s.direction}</td>
            <td>{s.sampled.toLocaleString('en-IN')}</td>
            <td>{s.classified.toLocaleString('en-IN')}</td>
            <td>{s.skippedOverCap.toLocaleString('en-IN')}</td>
            <td>{s.skippedBusy.toLocaleString('en-IN')}</td>
            <td className={s.notRead ? 'text-sev-medium' : undefined} data-testid={`payload-not-read-${s.route}-${s.direction}`}>
              {(s.notRead ?? 0).toLocaleString('en-IN')}
            </td>
            <td>{dataClassesLabel(s.dataClasses)}</td>
            <td className="text-muted-foreground">
              {s.avgClassifyMicros === null ? '-' : `${(s.avgClassifyMicros / 1000).toFixed(2)} ms each, off the request path`}
            </td>
            <td className="text-muted-foreground">{istTime(s.lastReceivedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
